
import React from 'react';
import { View, StyleSheet, Platform, Pressable, TouchableOpacity, useWindowDimensions } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { fonts, layout } from '../styles';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { RootStackParamList } from '../navigation/types';
import { Typography } from './ui';
import { BrandLogo } from './BrandLogo';
import { openCookiePreferences } from './cookiePreferences';
import { BUILD_SHA } from '../config/buildStamp';
import { useFinanceScreenEnabled, useFooterHiddenSocialsShown, useReferralUiEnabled } from '../hooks/useFeatureFlags';
import Ionicons from '@expo/vector-icons/Ionicons';
import { NavSurfaces } from '../services/marketingTags';
import { trackNavClicked } from '../services/navAnalytics';
import { SOCIAL_LINKS, visibleFooterSocials, type SocialNetworkId } from '../config/socialLinks';
import { useForeignJurisdiction, useLegalEntityLine } from '../legal/useLegalEntityLine';
import { isLegalDocShown } from '../legal/docIds';

/**
 * Footer surface stays dark on both light and dark themes — it's part of
 * the brand's "warm dusk" look, the boundary that ends every page. In
 * light mode the soft graphite reads as a calm shadow against the cream
 * canvas; in dark mode it matches the rest of the chrome (#17181C). Use
 * a slightly different graphite in light so the page-to-footer
 * transition is a fade, not a hard cutoff.
 */
const FOOTER_BG_LIGHT = '#1F2125';
const FOOTER_UI_FONT = Platform.OS === 'web' ? 'var(--ag-font-ui)' : fonts.text;
const FOOTER_DISPLAY_FONT = Platform.OS === 'web' ? 'var(--ag-font-ui)' : fonts.display;

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

/**
 * CORE-1483 — пять соцсетей из списка Маши; адреса в `config/socialLinks`.
 * У Ionicons нет знака Дзена, он нарисован ниже сам.
 */
const SOCIAL_ICON: Record<Exclude<SocialNetworkId, 'dzen'>, React.ComponentProps<typeof Ionicons>['name']> = {
  instagram: 'logo-instagram',
  telegram: 'paper-plane',
  vk: 'logo-vk',
  youtube: 'logo-youtube',
};

/** Знак Дзена — четырёхлучевая «звезда» с вогнутыми краями, в масштабе 18 px иконок рядом. */
const DzenMark: React.FC = () => (
  <Svg width={18} height={18} viewBox="0 0 18 18" accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
    <Path d="M9 1C9 6 6 9 1 9C6 9 9 12 9 17C9 12 12 9 17 9C12 9 9 6 9 1Z" fill="white" />
  </Svg>
);

/** Настоящая ссылка (`<a href>`): новая вкладка, клавиатура и «копировать адрес» — бесплатно. */
const externalLink = (url: string) => ({
  href: url,
  hrefAttrs: { target: '_blank', rel: 'noopener' },
}) as Record<string, unknown>;

// Surfaced only on dev builds (rn-agape.vercel.app). Lets QA and the
// owner say «у меня видно SHA abc123 — это твой последний пуш?» without
// asking us to inspect bundle headers. Hidden in prod so end users
// never see a commit hash next to the legal links.
// Vercel injects VERCEL_GIT_COMMIT_SHA / VERCEL_GIT_COMMIT_REF at build
// time — the buildCommand mirrors them into EXPO_PUBLIC_* so the
// expo export keeps them in the client bundle.
// CORE-631 — buildStamp first, env var as fallback: the babel-inlined constant
// survives metro's shared cache across builds and made this chip name a commit
// from another branch. See the note in config/buildInfo.ts.
const COMMIT_SHA = (BUILD_SHA || process.env.EXPO_PUBLIC_COMMIT_SHA || '').trim();
const IS_DEV_BUILD = (process.env.EXPO_PUBLIC_API_URL || '').includes('agdev.tmless.org');

// Web-only chrome (mirrors Header): hidden entirely in the native app.
export const Footer: React.FC = () => {
  if (Platform.OS !== 'web') return null;
  return <FooterImpl />;
};

const FooterImpl: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const { isDark, colors: themeColors } = useTheme();
  const { t } = useI18n('common');
  const legalEntityLine = useLegalEntityLine();
  // CORE-1567 — Streaming Agreement (AGAPE.IO Limited) — только вне контура РФ.
  const foreignJurisdiction = useForeignJurisdiction();
  // CORE-1156 — вход в реферальную программу ведёт в раздел «Финансы», и после
  // CORE-1186 у раздела появился свой флаг. Пункт остался на одном
  // `referral_user_ui`, поэтому на контуре, где рефералка включена, а денежный
  // раздел ещё нет (ровно состояние dev на 02.09), он вёл на «Раздел пока
  // закрыт». Оба хука зовём безусловно: `&&` из двух вызовов сорвал бы порядок
  // хуков на первом же выключенном флаге.
  const financeEnabled = useFinanceScreenEnabled();
  const referralUiEnabled = useReferralUiEnabled();
  const referralEnabled = financeEnabled && referralUiEnabled;
  // CORE-1768 — скрытые сети (Instagram) не рендерятся вовсе: ни места, ни фокуса.
  const socials = visibleFooterSocials(SOCIAL_LINKS, useFooterHiddenSocialsShown());
  const isMediumScreen = width >= 768;

  /**
   * Переход по разделу из подвала.
   *
   * CORE-1277 — цели «Календарь», «Практики» и «Мастера» считают глобальный
   * переход, а не клик именно в шапке: в подвале те же три ссылки, и человек с
   * длинной страницы уходит в раздел чаще снизу, чем возвращаясь наверх.
   */
  const goToSection = (route: 'Lives' | 'Practices' | 'Masters') => {
    trackNavClicked(route, NavSurfaces.footer);
    navigation.navigate(route as never);
  };

  return (
    <View style={[
      styles.footer,
      { backgroundColor: isDark ? themeColors.canvas : FOOTER_BG_LIGHT },
      // In dark mode the footer bg is identical to canvas, so the
      // page-to-footer boundary visually disappears — sections above
      // bleed into the footer. A 1px hairline restores the seam without
      // the heavy cutoff a contrasting fill would create.
      isDark && { borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)' },
      // Mobile breathing room — sections above (Masters, Practices)
      // sit directly above the footer with no margin. Belt-and-suspenders
      // with the section-side paddingBottom so even if the section's
      // padding fails to render (cache, override), the footer still
      // gives the eye a generous gap.
      !isMediumScreen && { marginTop: 32, paddingTop: 80 },
      // Break out of any constrained parent on web (paddingHorizontal, maxWidth, alignSelf)
      Platform.OS === 'web' && styles.footerFullWidth,
    ]}>
      <View style={styles.container}>
        <View style={[styles.mainRow, isMediumScreen && styles.mainRowMedium]}>
          {/* Brand Column */}
          <View style={[styles.brandCol, !isMediumScreen && styles.brandColMobile]}>
            <TouchableOpacity onPress={() => navigation.navigate('Home')}>
              <BrandLogo height={40} variant="dark" style={{ marginBottom: 4 }} />
            </TouchableOpacity>
            {/* CORE-1483 — две строки редакции «Футер 21.09», каждая своей
                строкой, а не одним абзацем через точку. */}
            <View style={styles.taglines}>
              <Typography variant="body" style={[styles.tagline, !isMediumScreen && styles.taglineMobile]}>
                {t('footer.tagline1')}
              </Typography>
              <Typography variant="body" style={[styles.tagline, !isMediumScreen && styles.taglineMobile]}>
                {t('footer.tagline2')}
              </Typography>
            </View>
            <View style={styles.socials}>
              {socials.map((social) => (
                <Pressable
                  key={social.id}
                  {...externalLink(social.url)}
                  accessibilityRole="link"
                  accessibilityLabel={social.label}
                  testID={`footer-social-${social.id}`}
                  style={({ pressed }) => [styles.socialIcon, pressed && styles.socialIconPressed]}
                >
                  {social.id === 'dzen'
                    ? <DzenMark />
                    : <Ionicons name={SOCIAL_ICON[social.id]} size={18} color="white" />}
                </Pressable>
              ))}
            </View>
            {/* CORE-1934 — реквизиты юрлица по юрисдикции соглашения, см. legal/useLegalEntityLine. */}
            <Typography style={[styles.legalEntity, !isMediumScreen && styles.taglineMobile]}>{legalEntityLine}</Typography>
          </View>

          {/* Links Columns */}
          <View style={[styles.linksContainer, isMediumScreen && styles.linksContainerMedium, !isMediumScreen && styles.linksContainerMobile]}>
            <View style={[styles.linkGroup, !isMediumScreen && styles.linkGroupMobile]}>
              <Typography style={styles.linkHeader}>{t('footer.platform')}</Typography>
              {__DEV__ && <TouchableOpacity onPress={() => navigation.navigate('CustDev' as any)}><Typography style={[styles.linkItem, { color: themeColors.solar }]}>CustDev</Typography></TouchableOpacity>}
              {/* CORE-1483 — «Календарь» вернулся в «Платформу»; порядок как в шапке. */}
              <TouchableOpacity onPress={() => goToSection('Lives')}><Typography style={styles.linkItem}>{t('nav.schedule')}</Typography></TouchableOpacity>
              <TouchableOpacity onPress={() => goToSection('Practices')}><Typography style={styles.linkItem}>{t('nav.practices')}</Typography></TouchableOpacity>
              <TouchableOpacity onPress={() => goToSection('Masters')}><Typography style={styles.linkItem}>{t('nav.masters')}</Typography></TouchableOpacity>
              {/* CORE-1156 — вход в реферальную программу из футера. Виден
                  только когда открыты обе половины: сама программа
                  (`referral_user_ui`) и раздел, в котором она живёт
                  (`finance_screen`). Иначе ссылка вела бы на панель с
                  прочерками или на заглушку «Раздел пока закрыт». */}
              {referralEnabled && (
                <TouchableOpacity onPress={() => navigation.navigate('Finance')}><Typography style={styles.linkItem}>{t('footer.referral')}</Typography></TouchableOpacity>
              )}
            </View>

            <View style={[styles.linkGroup, !isMediumScreen && styles.linkGroupMobile]}>
              <Typography style={styles.linkHeader}>{t('footer.company')}</Typography>
              <TouchableOpacity onPress={() => navigation.navigate('About')}><Typography style={styles.linkItem}>{t('footer.about')}</Typography></TouchableOpacity>
              {/* CORE-1151 — «Миссия» рядом с «О нас»: на неё же ведёт кнопка
                  «Подробнее о миссии платформы» из раздела «Что такое Агапе». */}
              <TouchableOpacity onPress={() => navigation.navigate('LegalDoc', { docId: 'mission' })}><Typography style={styles.linkItem}>{t('footer.mission')}</Typography></TouchableOpacity>
              <TouchableOpacity onPress={() => navigation.navigate('Contact')}><Typography style={styles.linkItem}>{t('footer.contact')}</Typography></TouchableOpacity>
              {/* «Карьера» — temporarily hidden until the careers page lands. */}
              {/* <TouchableOpacity><Typography style={styles.linkItem}>{t('footer.careers')}</Typography></TouchableOpacity> */}
            </View>

            <View style={[styles.linkGroup, !isMediumScreen && styles.linkGroupMobile]}>
              <Typography style={styles.linkHeader}>{t('footer.legal')}</Typography>
              <TouchableOpacity onPress={() => navigation.navigate('LegalDoc', { docId: 'terms-of-use' })}><Typography style={styles.linkItem}>{t('footer.termsOfUse')}</Typography></TouchableOpacity>
              <TouchableOpacity onPress={() => navigation.navigate('LegalDoc', { docId: 'privacy-policy' })}><Typography style={styles.linkItem}>{t('footer.privacyPolicy')}</Typography></TouchableOpacity>
              {/* CORE-1401 — Уведомление об ИИ дополняет Политику (п. 1.2) и по своему
                  п. 12.2 «размещается на Платформе»: рядом с ней и стоит. */}
              <TouchableOpacity testID="footer-ai-notice" onPress={() => navigation.navigate('LegalDoc', { docId: 'ai-notice' })}><Typography style={styles.linkItem}>{t('footer.aiNotice')}</Typography></TouchableOpacity>
              {/* CORE-1659 — выбор по cookie можно изменить в любой момент, и без входа тоже. Cookie есть только на вебе. */}
              {Platform.OS === 'web' && (
                <TouchableOpacity testID="footer-cookie-settings" onPress={openCookiePreferences}><Typography style={styles.linkItem}>{t('footer.cookieSettings')}</Typography></TouchableOpacity>
              )}
              {isLegalDocShown('streaming-agreement', foreignJurisdiction) && (
                <TouchableOpacity testID="footer-streaming-agreement" onPress={() => navigation.navigate('LegalDoc', { docId: 'streaming-agreement' })}><Typography style={styles.linkItem}>{t('footer.streamingAgreement')}</Typography></TouchableOpacity>
              )}
              <TouchableOpacity onPress={() => navigation.navigate('LegalDoc', { docId: 'ethics-code' })}><Typography style={styles.linkItem}>{t('footer.ethicsCode')}</Typography></TouchableOpacity>
              <TouchableOpacity onPress={() => navigation.navigate('LegalDoc', { docId: 'delete-account' })}><Typography style={styles.linkItem}>{t('footer.deleteAccount')}</Typography></TouchableOpacity>
            </View>
          </View>
        </View>

        <View style={[styles.bottomBar, !isMediumScreen && styles.bottomBarMobile]}>
          <Typography style={[styles.copyright, !isMediumScreen && styles.copyrightMobile]}>{t('footer.copyright')}</Typography>
          <View style={[
            { flexDirection: 'row', gap: 8, alignItems: 'center', justifyContent: 'center' },
            !isMediumScreen && { alignSelf: 'center' },
          ]}>
            {IS_DEV_BUILD && Boolean(COMMIT_SHA) && (
              <Typography style={styles.buildSha}>
                build {COMMIT_SHA.slice(0, 7)}
              </Typography>
            )}
            <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: themeColors.solar }} />
            <Typography style={[styles.madeWith, !isMediumScreen && styles.madeWithMobile]}>{t('footer.designedFor')}</Typography>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  footer: {
    // Background applied inline (light = FOOTER_BG_LIGHT graphite, dark =
    // themeColors.canvas) so the page-to-footer transition reads as a
    // shadow rather than a cutoff. See FOOTER_BG_LIGHT note at the top
    // of this file.
    paddingTop: 64,
    paddingBottom: 40,
  },
  footerFullWidth: {
    width: '100vw',
    position: 'relative',
    left: '50%',
    right: '50%',
    marginLeft: '-50vw',
    marginRight: '-50vw',
  } as any,
  container: {
    maxWidth: layout.maxWidth,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: layout.paddingH,
  },
  mainRow: {
    flexDirection: 'column',
    gap: 48,
    marginBottom: 64,
  },
  mainRowMedium: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  brandCol: {
    maxWidth: 360,
    gap: 32,
  },
  // AMB-227 mobile polish — center the brand column and all its
  // children (logo, tagline, socials). Gated by `!isMediumScreen` at
  // the call-site, so the desktop layout (width ≥ 768) is untouched.
  brandColMobile: {
    alignItems: 'center',
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'center',
  },
  logoImage: {
    height: 40,
    width: 140,
    marginBottom: 4,
  },
  taglines: {
    gap: 4,
  },
  tagline: {
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 26,
    fontSize: 16,
    fontFamily: FOOTER_UI_FONT,
  },
  // Mobile only — center-align the tagline text under the centered logo.
  taglineMobile: {
    textAlign: 'center',
  },
  socials: {
    flexDirection: 'row',
    gap: 12,
  },
  socialIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.05)',
  },
  socialIconPressed: {
    opacity: 0.7,
  },
  linksContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 48,
  },
  linksContainerMedium: {
    gap: 100,
  },
  // Mobile only — keep the two-column row («Платформа» + «Компания»)
  // and center the whole block horizontally. Items inside each column
  // stay left-aligned (no alignItems override on linkGroupMobile).
  // «Правовая информация» wraps to the next centered row.
  linksContainerMobile: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: '100%',
    gap: 56,
    rowGap: 30,
  },
  linkGroup: {
    gap: 20,
  },
  // Mobile only — header and items stay left-aligned inside each column
  // (no `alignItems: 'center'` here on purpose — the columns themselves
  // are centered via `linksContainerMobile`'s `justifyContent`).
  linkGroupMobile: {
    minWidth: 110,
    alignItems: 'center'
  },
  linkHeader: {
    color: '#E98245',  // Official solar orange from Фир стиль
    fontSize: 13,
    fontWeight: '600',
    fontFamily: FOOTER_DISPLAY_FONT,
    letterSpacing: 0.5,
    marginBottom: 4,
    opacity: 0.9,
  },
  linkItem: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 14,
    fontWeight: '500',
    fontFamily: FOOTER_UI_FONT,
    letterSpacing: -0.2,
  },
  bottomBar: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
    paddingTop: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
  },
  // Mobile only — copyright + «designed for» stack and center under the
  // border line instead of justify-space-between which leaves a gap.
  // Tight 6 px gap so the two lines read as one block instead of two
  // disconnected lines (per design tweak).
  bottomBarMobile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1,
    width: '100%',
  },
  copyright: {
    width: '100%',
    // CORE-1733 (возврат QA) — .3 на тёмном подвале давал 2.7:1; .6 — около 6.5.
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
    fontWeight: '500',
    fontFamily: FOOTER_UI_FONT,
  },
  copyrightMobile: {
    textAlign: 'center',
    width: '100%',
  },
  legalEntity: {
    // brandCol раздвигает детей на 32 — юрстрока должна читаться подписью к иконкам.
    marginTop: -16,
    // CORE-1733 (возврат QA) — .3 на тёмном подвале давал 2.7:1; .6 — около 6.5.
    color: 'rgba(255,255,255,0.6)',
    fontSize: 11,
    lineHeight: 16,
    fontWeight: '500',
    fontFamily: FOOTER_UI_FONT,
  },
  madeWith: {
    // CORE-1733 (возврат QA) — .3 на тёмном подвале давал 2.7:1; .6 — около 6.5.
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
    fontWeight: '500',
    fontFamily: FOOTER_UI_FONT,
  },
  madeWithMobile: {
    textAlign: 'center',
  },
  buildSha: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 11,
    fontWeight: '500',
    fontFamily: Platform.OS === 'web' ? FOOTER_UI_FONT : 'Menlo, Consolas, monospace',
    letterSpacing: 0.3,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
});
